import {
  Calendar,
  CheckCircle,
  Clock,
  CreditCard,
  X,
  AlertTriangle,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import type { ActiveMembership } from "../../types/membership";
import { useAuth } from "../../context/AuthContext";
import { dictionary } from "../../utils/dictionary";
import { format } from "date-fns";
import { convertToZone } from "../../utils/formatters";
import { useEffect, useState } from "react";
import { PlanChangeModal } from "./PlanChangeModal";
import { CancelPlanModal } from "./CancelPlanModal";
import { FeedbackModal } from "./FeedbackModal";
import { getCancelStatus } from "../../services/membership/MembershipAxios";
import toast from "react-hot-toast";
import { Alert } from "../common/information/Alert";

interface PlanSectionProps {
  plan: ActiveMembership;
}

export function PlanSection({ plan }: PlanSectionProps) {
  const { language } = useAuth();
  const formatDate = "dd/MM/yyyy";
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenCancel, setIsOpenCancel] = useState(false);
  const [isOpenFeedback, setIsOpenFeedback] = useState(false);
  const [cancelStatus, setCancelStatus] = useState<boolean | null>(null);

  useEffect(() => {
    getCancelStatus()
      .then((res) => {
        setCancelStatus(res.canceled);
      })
      .catch((err) => {
        console.error(err);
      });
  }, []);

  const handleUpgrade = () => {
    const whatsappNumber = import.meta.env.VITE_PUBLIC_PHONE_FLUXI;
    const text = "Quiero mejorar mi plan";
    const url = `https://api.whatsapp.com/send/?phone=${whatsappNumber}&text=${text}&type=phone_number&app_absent=0`;
    window.open(url, "_blank");
  };

  const handleCancel = () => {
    if (!cancelStatus) {
      setIsOpen(false);
      setIsOpenCancel(true);
      return;
    }

    toast.error(
      "Ya quedó cancelada tu renovación automática, lamentamos verte ir y esperamos que vuelvas pronto"
    );
  };

  const handleCancelPlan = () => {
    setIsOpenCancel(false);
    setIsOpenFeedback(true);
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-white" />
            </div>
            <div className="flex flex-col gap-2 w-full">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold text-gray-900">
                  {dictionary[plan.plan_name as keyof typeof dictionary][language] || plan.plan_name}
                </h2>
                {cancelStatus && (
                    <Alert
                      type="error"
                      icon={X}
                      description="Cancelado"
                      className="rounded border text-sm font-medium w-fit items-center max-w-[490px] !px-4 !py-2"
                    />
                )}
              </div>

              {!cancelStatus && plan.is_active && (
                <div>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700 border border-gray-300">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    {dictionary["active"][language]}
                  </span>
                </div>
              )}

              {cancelStatus && (
                <Alert
                  type="error"
                  icon={AlertTriangle}
                  description={`Tu acceso terminará el ${convertToZone(
                    format(new Date(plan.payment_ends_at || ""), formatDate),
                    formatDate
                  )}. Después de esta fecha perderás el acceso a Fluxi.`}
                  className="rounded border text-sm font-medium w-fit items-center max-w-[490px] !px-4 !py-2"
                />
              )}
            </div>
          </div>
          <button
            onClick={() => setIsOpen(true)}
            className="bg-gray-600 text-white px-4 py-2 rounded-md"
          >
            {dictionary["change-plan"][language]}
          </button>
          <PlanChangeModal
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            onUpgrade={handleUpgrade}
            onCancel={handleCancel}
          />
          <CancelPlanModal
            isOpen={isOpenCancel}
            onClose={() => setIsOpenCancel(false)}
            onCancelPlan={handleCancelPlan}
          />
          <FeedbackModal
            isOpen={isOpenFeedback}
            onClose={() => setIsOpenFeedback(false)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl">
            <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">
                {dictionary["activation-date"][language]}
              </p>
              <p className="font-semibold text-gray-900">
                {convertToZone(
                  format(new Date(plan.inits_at || ""), formatDate),
                  formatDate
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl">
            <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">
                {dictionary["upcoming-renovation"][language]}
              </p>
              <p className="font-semibold text-gray-900">
                {convertToZone(
                  format(new Date(plan.payment_ends_at || ""), formatDate),
                  formatDate
                )}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
